import { useEffect, useRef } from "react"

interface EditorShortcutsOptions {
  /** 是否启用 */
  enabled?: boolean
  /** Cmd/Ctrl + S */
  onSave?: () => void
  /** Cmd/Ctrl + Z */
  onUndo?: () => void
  /** Cmd/Ctrl + Shift + Z 或 Ctrl + Y */
  onRedo?: () => void
}

const TEXT_INPUT_TYPES = new Set([
  "text", "search", "url", "tel", "email", "password", "number",
  "date", "datetime-local", "month", "time", "week",
])

/**
 * 焦点在可编辑区域时，撤销 / 重做交给浏览器原生处理
 */
const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  if (target.isContentEditable || target.closest(".cm-editor")) {
    return true
  }
  if (target instanceof HTMLTextAreaElement) {
    return true
  }
  if (target instanceof HTMLInputElement) {
    return TEXT_INPUT_TYPES.has(target.type)
  }
  return false
}

/**
 * 编辑页通用快捷键：保存 / 撤销 / 重做
 * 回调通过 ref 读取，调用方无需 useCallback
 */
export function useEditorShortcuts({
  enabled = true,
  onSave,
  onUndo,
  onRedo,
}: EditorShortcutsOptions) {
  const handlersRef = useRef({ onSave, onUndo, onRedo })

  handlersRef.current = { onSave, onUndo, onRedo }

  useEffect(() => {
    if (!enabled) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing || event.altKey || !(event.metaKey || event.ctrlKey)) {
        return
      }

      const key = event.key.toLowerCase()
      const handlers = handlersRef.current

      if (key === "s" && handlers.onSave) {
        // 拦截浏览器「保存网页」
        event.preventDefault()
        if (!event.repeat) {
          handlers.onSave()
        }
        return
      }

      const isUndo = key === "z" && !event.shiftKey
      const isRedo = (key === "z" && event.shiftKey) || (key === "y" && event.ctrlKey && !event.shiftKey)

      if ((!isUndo && !isRedo) || isEditableTarget(event.target)) {
        return
      }

      const handler = isUndo ? handlers.onUndo : handlers.onRedo

      if (handler) {
        event.preventDefault()
        handler()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [enabled])
}
